import React, { HTMLAttributes, FC, useState, useCallback, useRef, useEffect } from 'react';
import clsx from 'clsx';

import { Icon } from 'components/Icon';

import style from './Dropdown.module.scss';
import { CSSTransition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import { useHandleCloseDrawer } from 'hooks/useHandleCloseDrawer';
import { KEY } from 'util/Input';

export type DropdownProps = HTMLAttributes<HTMLSelectElement> & {
  readonly: boolean;
  label: string;
  defaultValue: string;
  dataTestId?: string;
  iconName?: string;
  selectClassName?: string;
}

export const Dropdown: FC<DropdownProps> = ({ children, className, label, defaultValue, dataTestId, iconName, selectClassName, readonly = false, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectWrapperRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentValue, setCurrentValue] = useState(defaultValue);
  const toggleDropdown = useCallback(() => setIsOpen((current: any) => !current), []);

  useEffect(() => {
    if (defaultValue !== currentValue) setCurrentValue(defaultValue);
  }, [currentValue, defaultValue]);

  // Handle closing on outside mouse clicks
  useHandleCloseDrawer(wrapperRef, () => { setIsOpen(false); });

  const queue = React.Children.toArray(children);
  const processed: any = [];

  const handleEsc = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === KEY.ESC && isOpen) {
      e.stopPropagation();
      toggleDropdown();
    }
  };

  while (queue.length) {
    // eslint-disable-next-line functional/immutable-data
    const child = queue.shift();

    if (!React.isValidElement(child)) {
      processed.push(child);

      continue;
    }

    const { value, className: childClassName, onClick, ...otherProps } = child.props;

    processed.push(React.cloneElement(child, {
      className: clsx(childClassName, value === currentValue && style.bold),
      onClick(e: any) {
        setCurrentValue(value);

        toggleDropdown();
        onClick?.(e);
      },
      onKeyDown(e: React.KeyboardEvent<HTMLElement>) {
        handleEsc(e);
      },
      ...otherProps
    }));
  }

  return (
    <div className={clsx(style.wrapper, className)} ref={wrapperRef}>
       <button className={clsx(style.dropdownWrapper)} onKeyDown={handleEsc} onClick={toggleDropdown} type="button" disabled={readonly} data-testid={dataTestId}>
        {iconName && <div className={clsx(style.container)}>
          <Icon name={iconName} size={16}></Icon>
        </div>}
        <div className={style.labelWrapper}>
          <label className={style.label} htmlFor="select">{label}</label>
          <div className={style.currentItem}>{currentValue}</div>
        </div>
        {!readonly && <div className={style.iconWrapper}>
          <Icon className={style.icon} name={'expand'}></Icon>
        </div>}
      </button>
      <CSSTransition
        in={isOpen}
        timeout={100}
        unmountOnExit
        nodeRef={selectWrapperRef}>
        {(state: TransitionStatus) => {
          const isOpening = ['entering', 'entered'].includes(state);
          const isClosing = ['exiting', 'exited'].includes(state);

          return (
            <div data-testid={`${dataTestId}-dropdown`} className={clsx(style.select, selectClassName, {
              [style.entering]: isOpening,
              [style.exiting]: isClosing
            })} ref={selectWrapperRef}>
              { processed }
            </div>
          );
        }}
      </CSSTransition>
    </div>
  );
};
